import React, { FocusEventHandler, useContext, useState } from "react";
import { FirebaseContext } from "../../firebase/FirebaseProvider";
import
{
    Accordion, AccordionDetails, AccordionSummary, Box, Button, FormControl, InputLabel, List, ListItem,
    ListItemText, MenuItem, Select, SelectChangeEvent, TextField
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import Constraint, { IConstraint } from "./Constraint";
import { StyleOptions } from "../../../utility/Style";

export interface IConstant
{
    constraints?: { [key: string]: IConstraint }
    description: string
    name: string
    type: string
    style?: { [value: string]: StyleOptions }
    chip?: StyleOptions | "auto",
    chipValue?: number | string
}
interface IProps
{
    admin: boolean
    constant: IConstant
    expanded: string
    name: string
    project: string
    setExpanded: (expanded: string) => void
}

export default function Constant({ admin, constant, expanded, name, project, setExpanded }: IProps)
{
    const [constraintExpanded, setConstraintExpanded] = useState("");
    const { push, remove, set } = useContext(FirebaseContext);
    const constantPath = `constants/${project}/${name}`;
    const handleAddConstraintClick = () => push(`${constantPath}/constraints`, { type: "unique" });
    const handleDeleteClick = () => remove(constantPath);
    const handleDescriptionBlur: FocusEventHandler<HTMLInputElement> = ({ target }) =>
        set(`${constantPath}/description`, target.value);
    const handleNameBlur: FocusEventHandler<HTMLInputElement> = ({ target }) =>
        set(`${constantPath}/name`, target.value);
    const handleTypeChange = ({ target }: SelectChangeEvent) => set(`${constantPath}/type`, target.value);

    return (
        <Box m={1}>
            <Accordion expanded={expanded === name} onChange={() => setExpanded(expanded === name ? "" : name)}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <ListItem>
                        <ListItemText primary={name} />
                    </ListItem>
                </AccordionSummary>
                <AccordionDetails>
                    <Box display="flex">
                        <TextField disabled={!admin} defaultValue={constant.name} label="Name" onBlur={handleNameBlur}
                            size="small" sx={{ marginLeft: 1 }} />
                        <FormControl sx={{ flexGrow: 1, marginLeft: 1 }}>
                            <InputLabel>Type</InputLabel>
                            <Select disabled={!admin} label="Type" value={constant.type} size="small"
                                onChange={handleTypeChange}>
                                <MenuItem value="boolean">Boolean</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="string">String</MenuItem>
                            </Select>
                        </FormControl>
                        <Button disabled={!admin} onClick={handleDeleteClick} variant="contained" sx={{ marginLeft: 1 }}>
                            Delete
                        </Button>
                    </Box>
                    <TextField disabled={!admin} defaultValue={constant.description} label="Description"
                        onBlur={handleDescriptionBlur} multiline fullWidth size="small" sx={{ m: 1 }} rows={3} />
                    <List>
                        <ListItem>
                            <ListItemText primary="Constraints limit when the constant can be used and the values it can take" />
                            <Button disabled={!admin} onClick={handleAddConstraintClick} variant="contained" sx={{ m: 1 }}>
                                Add
                            </Button>
                        </ListItem>
                        {Object.keys(constant.constraints || {}).map(key => (
                            <Constraint admin={admin} key={key} id={key} constant={name}
                                constraint={constant.constraints![key]} expanded={constraintExpanded}
                                project={project} setExpanded={setConstraintExpanded} />
                        ))}
                    </List>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
